import React from 'react';
import { Proposal } from '../../hooks/useProposals';
import Thermometer from './Thermometer';
import ProposalActionsMenu from './ProposalActionsMenu';

interface Client {
    id: string;
    name: string;
}

interface ProposalTableViewProps {
    proposals: Proposal[];
    clients: Client[];
    loadingClients?: boolean;
    handleEdit: (proposal: Proposal) => void;
    handleDelete: (proposalId: string) => void;
    setSelectedProposalForDetails: (proposal: Proposal | null) => void;
}

export default function ProposalTableView({
    proposals,
    clients,
    loadingClients,
    handleEdit,
    handleDelete,
    setSelectedProposalForDetails,
}: ProposalTableViewProps) {

    const getClientName = (clientId: string): string => {
        if (loadingClients) return 'Carregando cliente...';
        const client = clients.find(c => c.id === clientId);
        return client ? client.name : 'Cliente não encontrado';
    };

    return (
        <div className="w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 table-auto">
                <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proposta</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temperatura</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duração</th>
                        <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Recursos / Responsável</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Observações</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {proposals.map((proposal) => {
                        const totalPeople = proposal.resources.reduce((sum, resource) => sum + resource.quantity, 0);
                        const clientName = getClientName(proposal.clientId);

                        return (
                            <tr key={proposal.id}>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{proposal.name}</div>
                                    <div className="text-sm text-gray-500">{clientName}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Thermometer heatLevel={proposal.heatLevel} mode="display" />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${proposal.status === 'won' ? 'bg-green-100 text-green-800' :
                                        proposal.status === 'lost' ? 'bg-red-100 text-red-800' :
                                            proposal.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                                                'bg-blue-100 text-blue-800'
                                        }`}>
                                        {proposal.status === 'won' ? 'Ganha' :
                                            proposal.status === 'lost' ? 'Perdida' :
                                                proposal.status === 'cancelled' ? 'Cancelada' :
                                                    'Em Negociação'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">{proposal.duration}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <button
                                        onClick={() => setSelectedProposalForDetails(proposal)}
                                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                    >
                                        {totalPeople} {totalPeople === 1 ? 'recurso' : 'recursos'}
                                    </button>
                                    <div className="text-xs text-gray-500 mt-1">{proposal.manager}</div>
                                </td>
                                <td className="px-6 py-4">
                                    {proposal.notes && (
                                        <div className="text-sm text-gray-500 whitespace-pre-wrap" title={proposal.notes}>{proposal.notes}</div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <ProposalActionsMenu
                                        proposal={proposal}
                                        handleEdit={handleEdit}
                                        handleDelete={handleDelete}
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
} 